/**
 * `/ai-usage/me` — ☰ › AI usage: what the reader's own AI use cost.
 *
 * `docs/features/summary.md` § "Every charge is a ledger row, per user". The
 * member's half of ☰ Admin › Ledger (`api/ledger.ts`): the caller's rows only,
 * and never the model or the list cost — the server leaves both out, so there
 * is nothing here to forget to hide. `days` omitted is every row.
 *
 * Reads throw, as `api/ledger.ts`'s do: "you used nothing" and "the server did
 * not answer" must not look alike.
 */

import { currentUserId, requestHeaders } from "../lib/user";
import type { Me } from "./me";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

export interface MyCharge {
  at: string;
  /** `summary` (the shared note) or `summary-personal`. */
  reason: string;
  /** The symbol. */
  ref: string | null;
  billedUsd: number;
  inputTokens: number;
  outputTokens: number;
}

export interface MyUsage {
  days: number | null;
  totals: { charges: number; billedUsd: number; inputTokens: number; outputTokens: number };
  /** Newest first, 200 at most. */
  charges: MyCharge[];
  /**
   * Summary requests charged this UTC day — quevee's budget day — with a note
   * and its personal part counted once. `limit` null is no ceiling. `null`
   * without the Summary: the budget is the Summary's, so there is no line.
   */
  today: { used: number; limit: number | null; resetsAt: string } | null;
}

/**
 * Whether ☰ offers AI usage: anyone signed in, on the server's answer — chat
 * and Describe are charged on every plan (owner, 2026-11-01).
 *
 * `me` null is "not signed in or not answered yet", and offers nothing — `can`
 * alone would say yes to it (`lib/entitlements.ts` is permissive without an
 * answer), and a row for someone with no account is a control that fails.
 */
export function aiUsageOffered(me: Me | null): boolean {
  return me !== null;
}

export async function fetchMyUsage(days: number | null): Promise<MyUsage> {
  if (!BASE) throw new Error("no server configured");
  const period = days === null ? "" : `days=${days}&`;
  const response = await fetch(
    `${BASE}/ai-usage/me?${period}userId=${encodeURIComponent(currentUserId())}`,
    { headers: await requestHeaders() },
  );
  if (!response.ok) throw new Error((await response.text()) || `GET /ai-usage/me failed (${response.status})`);
  return (await response.json()) as MyUsage;
}
